import * as React from 'react';
import { Trans } from '~/common/context';
import { Button, FormContainer, Icon } from '@wyz/ui';
import { useBoolean } from '@wyz/utils';
import { CriteriaBaseForm } from '~/connect/components';
import { useForm, useFormContext, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { criteriaFormSchema } from '~/connect/schemas';

type CriteriaValueInputContextValue = {
  isEditing: boolean;
  open: () => void;
  close: () => void;
  methods: UseFormReturn<{ name: string }>;
  onSubmit: (criterionValue: string) => void;
};

const CriteriaValueInputContext =
  React.createContext<CriteriaValueInputContextValue | null>(null);

const useCriteriaValueInput = () => {
  const context = React.useContext(CriteriaValueInputContext);
  if (!context) {
    throw new Error(
      'CriteriaValueInput compound components must be used within CriteriaValueInput.Root',
    );
  }
  return context;
};

type RootProps = {
  onSubmit: (criterionValue: string) => void;
  children: React.ReactNode;
  defaultValue?: string;
};

function Root({ onSubmit, children, defaultValue }: RootProps) {
  const [isEditing, handlers] = useBoolean();
  const methods = useForm<{ name: string }>({
    resolver: zodResolver(criteriaFormSchema),
    defaultValues: {
      name: defaultValue || '',
    },
  });

  const handleSubmit = React.useCallback(
    (criterionValue: string) => {
      onSubmit(criterionValue);
      handlers.setFalse();
      methods.reset();
    },
    [onSubmit, handlers, methods],
  );

  const contextValue = React.useMemo(
    () => ({
      isEditing,
      open: handlers.setTrue,
      close: handlers.setFalse,
      methods,
      onSubmit: handleSubmit,
    }),
    [isEditing, handlers, methods, handleSubmit],
  );

  return (
    <CriteriaValueInputContext.Provider value={contextValue}>
      {children}
    </CriteriaValueInputContext.Provider>
  );
}

type TriggerProps = {
  id?: string;
  disabled?: boolean;
  children?: React.ReactNode;
};

function Trigger({ id, disabled, children }: TriggerProps) {
  const { isEditing, open } = useCriteriaValueInput();

  if (isEditing) {
    return null;
  }

  return (
    <Button
      id={id || 'criteria-value-input-trigger'}
      variant='white-chip'
      size='xxs'
      onClick={open}
      disabled={disabled}
    >
      {children || (
        <>
          <Trans i18nKey='app.criteria.list.actions.add_value' />
          <Icon
            icon='plus'
            className='ms-2 cursor-pointer'
            width={15}
            height={15}
          />
        </>
      )}
    </Button>
  );
}

type FormProps = {
  children?: React.ReactNode;
};

function Form({ children }: FormProps) {
  const { isEditing, close, methods, onSubmit } = useCriteriaValueInput();

  if (!isEditing) {
    return null;
  }

  return (
    <FormContainer
      methods={methods}
      submitHandler={(data) => {
        onSubmit(data.name);
      }}
    >
      {children || <CriteriaBaseForm onCancel={close} name='name' />}
    </FormContainer>
  );
}

type InputProps = {
  children?: React.ReactNode;
  name: string;
};

function Input({ children, name }: InputProps) {
  const { isEditing, close, onSubmit } = useCriteriaValueInput();
  const methods = useFormContext<{ name: string }>();
  if (!isEditing) {
    return null;
  }

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const value = methods.getValues(name as unknown as keyof { name: string });
    if (value) {
      onSubmit(value);
      methods.resetField(name as unknown as keyof { name: string });
    }
  };

  return (
    children ?? (
      <CriteriaBaseForm name={name} onCancel={close} onSubmit={handleSubmit} />
    )
  );
}

const CriteriaValueInput = {
  Root,
  Trigger,
  Form,
  Input,
};

export default CriteriaValueInput;
